import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../ui/tooltip";
import { Search, Filter, Calendar, Info } from "lucide-react";
import { STATUS_OPTIONS } from "./projectUtils";

export function ProjectsFilters({
  balances, searchTerm, setSearchTerm,
  statusFilter, setStatusFilter,
  typeFilter, setTypeFilter,
  managerFilter, setManagerFilter,
  yearFilter, setYearFilter,
  dataView, setDataView,
  includeInvestmentAid, setIncludeInvestmentAid,
}) {
  const uniqueTypes = [...new Set(balances.map(b => b.project_type).filter(Boolean))].sort();
  const uniqueManagers = [...new Set(balances.map(b => b.project_manager).filter(Boolean))].sort();

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row flex-wrap gap-4">
          <div className="flex-1 min-w-[220px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o notas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-projects"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-auto min-w-[170px]" data-testid="filter-status">
              <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-auto min-w-[170px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {uniqueTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={managerFilter} onValueChange={setManagerFilter}>
            <SelectTrigger className="w-auto min-w-[190px]"><SelectValue placeholder="Responsable" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los responsables</SelectItem>
              {uniqueManagers.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-auto min-w-[130px]" data-testid="filter-year">
              <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
              <SelectValue placeholder="Año" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 mt-4 pt-4 border-t">
          <span className="text-xs font-medium text-muted-foreground">Vista de datos:</span>
          <div className="inline-flex rounded-md border p-0.5 bg-muted/40" data-testid="data-view-toggle">
            <Button
              size="sm" variant={dataView === "source" ? "default" : "ghost"}
              className="h-7 text-xs px-3"
              onClick={() => setDataView("source")}
              data-testid="data-view-source-btn"
            >
              Fuente (libro)
            </Button>
            <Button
              size="sm" variant={dataView === "corrected" ? "default" : "ghost"}
              className="h-7 text-xs px-3"
              onClick={() => setDataView("corrected")}
              data-testid="data-view-corrected-btn"
            >
              Corregida (suma anual)
            </Button>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                <strong>Fuente (libro):</strong> total de proyecto tal como aparece en el Excel (valor oficial). <br />
                <strong>Corregida (suma anual):</strong> vista derivada que suma la diferencia documentada entre la anualización 2012-2028 y el total de proyecto (solo afecta a los proyectos con discrepancia conocida, p. ej. TECNOCIR). Nunca se guarda — es solo para análisis.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Switch
            id="include-investment-aid"
            checked={includeInvestmentAid}
            onCheckedChange={setIncludeInvestmentAid}
            data-testid="include-investment-aid-toggle"
          />
          <Label htmlFor="include-investment-aid" className="text-xs font-medium text-muted-foreground cursor-pointer">
            Incluir ayudas a la inversión (LICA, ADER, IDAE — no son I+D)
          </Label>
        </div>
      </CardContent>
    </Card>
  );
}
